import React, { useEffect, useState } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App';
import Landing from './Landing';
import MapDisplay from './components/MapDisplay';
import AuthScreen from './components/AuthScreen';
import ControlCenterOnboarding from './components/ControlCenterOnboarding';
import { ApiError, request } from './api';
import type { Player } from './domain/player';
import 'leaflet/dist/leaflet.css';
import './styles.css';

function Root() {
  const [path, setPath] = useState(window.location.pathname);
  const workspace = path.startsWith('/leitstelle');
  const display = path === '/leitstelle/karte';
  const [player, setPlayer] = useState<Player | null | undefined>(undefined);

  useEffect(() => {
    const navigate = () => setPath(window.location.pathname);
    window.addEventListener('popstate', navigate);
    return () => window.removeEventListener('popstate', navigate);
  }, []);

  useEffect(() => {
    if (!workspace) return;
    setPlayer(undefined);
    request<{ player: Player }>('auth/me')
      .then((result) => setPlayer(result.player))
      .catch((error) => {
        if (error instanceof ApiError && error.status === 401) setPlayer(null);
        else setPlayer(null);
      });
  }, [workspace]);

  function start() {
    window.history.pushState(null, '', '/leitstelle');
    setPath('/leitstelle');
    window.scrollTo({ top: 0, behavior: 'instant' });
  }

  async function logout() {
    await request('auth/logout', {});
    setPlayer(null);
  }

  if (!workspace) return <Landing onStart={start} />;
  if (player === undefined)
    return (
      <div className="auth-loading">
        <span />
        <b>LEITWERK</b>
        <small>Spielerkonto wird geladen …</small>
      </div>
    );
  if (!player) return <AuthScreen onAuthenticated={setPlayer} display={display} />;
  if (!player.controlCenterId)
    return (
      <ControlCenterOnboarding player={player} onComplete={(result) => setPlayer(result.player)} />
    );
  if (display)
    return <MapDisplay player={player} onExpired={() => setPlayer(null)} onLogout={logout} />;
  return <App player={player} onPlayer={setPlayer} onLogout={logout} />;
}

createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <Root />
  </React.StrictMode>,
);
